import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Radio, Upload, Button, Typography, Breadcrumb, Row, Col, Select, Spin, Progress, DatePicker,  } from 'antd';
import { InboxOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import type { UploadProps, UploadFile } from 'antd';
import { UpdateMediaRequest, MediaItem, getExposureDisplayText } from '../types/media';
import mediaService from '../services/media';
import { showConfirmModal } from '../components/PopupModal';
import MediaPreviewModal from '../components/MediaPreviewModal';
import { message } from '../utils/message';
import { useNavigationBlocker } from '../hooks/useNavigationBlocker';
import { useNavigationBlockerContext } from '../contexts/NavigationBlockerContext';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MediaEdit: React.FC = () => {
  const navigate = useNavigate();
  const { clearAllBlockers } = useNavigationBlockerContext();
  const { id } = useParams();
  const mediaId = id;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [mediaDetail, setMediaDetail] = useState<MediaItem | null>(null);
  const [layouts, setLayouts] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  const [currentFileDeleted, setCurrentFileDeleted] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [isModified, setIsModified] = useState(false);
  useNavigationBlocker(isModified, '/media-management');
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string>('');

  // 미디어 상세 정보 및 레이아웃 목록 로드
  useEffect(() => {
    const loadData = async () => {
      if (!mediaId) {
        message.error('잘못된 접근입니다.');
        navigate('/media-management');
        return;
      }

      try {
        setLoadingData(true);

        // 레이아웃 목록과 미디어 상세 정보를 병렬로 로드
        const [layoutList, mediaData] = await Promise.all([
          mediaService.getLayouts(),
          mediaService.getMediaDetail(Number(mediaId))
        ]);

        setLayouts(layoutList);
        setMediaDetail(mediaData);

        // 폼 초기값 설정
        form.setFieldsValue({
          layoutName: mediaData.layoutName,
          exposureType: mediaData.exposureType,
          mediaName: mediaData.mediaName,
          content: mediaData.content || '',
          alwaysVisible: !(mediaData as any).startDate && !(mediaData as any).endDate,
          exposurePeriod: ((mediaData as any).startDate && (mediaData as any).endDate)
            ? [dayjs((mediaData as any).startDate), dayjs((mediaData as any).endDate)]
            : null,
        });

      } catch (error) {
        console.error('데이터 로드 실패:', error);
        message.error('데이터를 불러오는 중 오류가 발생했습니다.');
        navigate('/media-management');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [mediaId, navigate, form]);

  // 파일 업로드 설정
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.mp4',
    maxCount: 1,
    beforeUpload: (file) => {
      const isVideo = file.type === 'video/mp4';
      if (!isVideo) {
        message.error('MP4 동영상 파일만 업로드 가능합니다.');
        return false;
      }

      const isLt100M = file.size / 1024 / 1024 < 100;
      if (!isLt100M) {
        message.error('파일 크기는 100MB 이하여야 합니다.');
        return false;
      }

      setVideoFile(file);
      // 파일 업로드 시 검증 트리거
      setTimeout(() => {
        form.validateFields(['videoFile']).catch(() => { });
      }, 0);
      return false; // 자동 업로드 방지
    },
    onRemove: () => {
      setVideoFile(null);
      // 파일 제거 시 검증 트리거
      setTimeout(() => {
        form.validateFields(['videoFile']).catch(() => { });
      }, 0);
    },
    fileList: videoFile ? [{
      uid: '-1',
      name: videoFile.name,
      status: 'done' as const,
      originFileObj: videoFile as any,
    }] as UploadFile[] : [],
  };

  // 폼 제출 핸들러
  const handleSubmit = async (values: any) => {
    if (!mediaId) {
      message.error('잘못된 접근입니다.');
      return;
    }

    setLoading(true);

    // 파일이 있는 경우에만 업로드 진행률 표시
    if (videoFile) {
      setIsUploading(true);
      setUploadProgress(0);
    }

    try {
      const updateData: UpdateMediaRequest = {
        id: Number(mediaId),
        layoutName: values.layoutName,
        exposureType: values.exposureType,
        startDate: values.alwaysVisible || values.exposureType === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[0] ? values.exposurePeriod[0].format('YYYY-MM-DD') : null),
        endDate: values.alwaysVisible || values.exposureType === 'hide'
          ? null
          : (values.exposurePeriod && values.exposurePeriod[1] ? values.exposurePeriod[1].format('YYYY-MM-DD') : null),
        mediaName: values.mediaName,
        content: values.content || '',
        deleteCurrentFile: currentFileDeleted,
      };

      // 새로운 비디오 파일이 있는 경우에만 추가
      if (videoFile) {
        updateData.videoFile = videoFile;

        // 진행률 시뮬레이션 (파일이 있는 경우에만)
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev >= 90) {
              clearInterval(progressInterval);
              return 90;
            }
            return prev + Math.random() * 10;
          });
        }, 200);

        await mediaService.updateMedia(updateData);

        // 업로드 완료
        setUploadProgress(100);
        clearInterval(progressInterval);

        setTimeout(() => {
          message.success('성공적으로 저장되었습니다.');
          clearAllBlockers();
          navigate('/media-management');
        }, 500);
      } else {
        // 파일이 없는 경우 바로 처리
        await mediaService.updateMedia(updateData);
        message.success('성공적으로 저장되었습니다.');
        clearAllBlockers();
        navigate('/media-management');
      }

    } catch (error) {
      console.error('미디어 수정 실패:', error);
      message.error('미디어 수정 중 오류가 발생했습니다.');
      if (videoFile) {
        setUploadProgress(0);
      }
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  // 기존 파일 삭제 핸들러
  const handleDeleteCurrentFile = () => {
    setCurrentFileDeleted(true);
    message.warning('기존 파일이 삭제 표시되었습니다. 새로운 파일을 업로드해주세요.');
    // 검증 트리거
    setTimeout(() => {
      form.validateFields(['videoFile']).catch(() => { });
    }, 0);
  };

  // 취소 핸들러
  const handleCancel = () => {
    if (isModified) {
      showConfirmModal({
        title: '알림',
        content: '작성한 내용은 복구할 수 없습니다.\n이전 단계로 이동하시겠습니까?',
        okText: '확인',
        cancelText: '취소',
        onConfirm: () => {
          setIsModified(false);
          setTimeout(() => {
            navigate('/media-management');
          }, 0);
        },
      });
    } else {
      navigate('/media-management');
    }
  };

  // 미리보기 핸들러
  const handlePreview = () => {
    // 새로 업로드한 파일이 있는 경우
    if (videoFile) {
      const tempUrl = URL.createObjectURL(videoFile);
      setPreviewVideoUrl(tempUrl);
      setPreviewVisible(true);
      return;
    }

    // 기존 파일이 있고 삭제되지 않은 경우
    if (mediaDetail && mediaDetail.videoFile && !currentFileDeleted) {
      setPreviewVisible(true);
      return;
    }

    message.warning('미리보기할 비디오 파일이 없습니다.');
  };

  // 미리보기 모달 닫기 핸들러
  const handlePreviewClose = () => {
    setPreviewVisible(false);
    // 임시 URL 메모리 해제
    if (previewVideoUrl) {
      URL.revokeObjectURL(previewVideoUrl);
      setPreviewVideoUrl('');
    }
  };

  if (loadingData) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '16px', color: '#ffffff' }}>
          데이터를 불러오는 중...
        </div>
      </div>
    );
  }

  if (!mediaDetail) {
    return (
      <div style={{ padding: '50px', textAlign: 'center', color: '#ffffff' }}>
        미디어 정보를 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        style={{ marginBottom: '24px' }}
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '미디어 관리', href: '/media-management' },
          { title: '미디어 편집' },
        ]}
      />

      {/* 페이지 헤더 */}
      <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
            미디어 편집
          </Title>
        </div>
        <Button onClick={handlePreview}>
          미리보기
        </Button>
      </div>

      {/* 메인 폼 */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={() => message.error('필수 입력 항목을 확인해주세요.')}
        requiredMark={false}
      
        onValuesChange={() => setIsModified(true)}
      >
        {/* 기본 정보 섹션 */}
        <Card
          title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기본 정보</Text>}
          style={{
            marginBottom: '24px',
            background: '#1f1f1f',
            border: '1px solid #303030',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Row gutter={[24, 16]}>
            <Col span={6}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>레이아웃명 <span style={{ color: 'red' }}>*</span></Text>}
                name="layoutName"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error('레이아웃명을 선택해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Select
                  disabled
                  allowClear={false}
                  placeholder="-"
                  style={{ background: '#141414', border: '1px solid #303030' }}
                  styles={{
                    popup: {
                      root: {
                        background: '#1f1f1f'
                      }
                    }
                  }}
                >
                  {layouts.map((layout) => (
                    <Option key={layout} value={layout}>
                      {layout}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={24}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>노출여부 <span style={{ color: 'red' }}>*</span></Text>}
                name="exposureType"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.reject(new Error('노출여부를 선택해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Radio.Group>
                  <Radio value="show" style={{ color: '#ffffff' }}>노출</Radio>
                  <Radio value="hide" style={{ color: '#ffffff' }}>비노출</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.exposureType !== currentValues.exposureType ||
                  prevValues.alwaysVisible !== currentValues.alwaysVisible
                }
              >
                {({ getFieldValue }) => {
                  const exposureType = getFieldValue('exposureType');
                  const alwaysVisible = getFieldValue('alwaysVisible');
                  const isExposureShow = exposureType === 'show';
                  const isDatePickerDisabled = !isExposureShow || alwaysVisible;

                  return (
                    <>
                      <div style={{ marginBottom: '24px' }}>
                        <Text style={{ color: '#ffffff' }}>
                          노출 기간 {isExposureShow && !alwaysVisible && <span style={{ color: 'red' }}>*</span>}
                        </Text>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
                          <Form.Item
                            name="exposurePeriod"
                            rules={[
                              {
                                validator: (_, value) => {
                                  if (isExposureShow && !alwaysVisible && (!value || !value[0] || !value[1])) {
                                    return Promise.reject(new Error('노출 기간을 선택해주세요.'));
                                  }
                                  return Promise.resolve();
                                },
                              },
                            ]}
                            style={{ marginBottom: 0 }}
                          >
                            <RangePicker
                              format="YYYY.MM.DD"
                              disabled={isDatePickerDisabled}
                              style={{ background: '#141414', border: '1px solid #303030' }}
                            />
                          </Form.Item>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <input
                                type="checkbox"
                                disabled={!isExposureShow}
                                style={{ cursor: isExposureShow ? 'pointer' : 'not-allowed' }}
                                checked={alwaysVisible}
                                onChange={(e) => {
                                  form.setFieldValue('alwaysVisible', e.target.checked);
                                  if (e.target.checked) {
                                    form.setFieldValue('exposurePeriod', null);
                                  }
                                }}
                              />
                              <Text style={{ fontSize: '14px', color: isExposureShow ? '#ffffff' : '#666666', margin: 0 }}>
                                상시 노출
                              </Text>
                            </div>
                            {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px', flexShrink: 0 }} />
                              <Text style={{ fontSize: '12px', color: '#a6a6a6', margin: 0, whiteSpace: 'nowrap' }}>
                                기간이 종료되면 자동 비공개 처리됩니다.
                              </Text>
                            </div> */}
                          </div>
                        </div>
                      </div>
                      <Form.Item
                        name="alwaysVisible"
                        valuePropName="checked"
                        style={{ display: 'none' }}
                      >
                        <input type="hidden" />
                      </Form.Item>
                    </>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* 미디어 정보 섹션 */}
        <Card
          title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>미디어 정보</Text>}
          style={{
            marginBottom: '24px',
            background: '#1f1f1f',
            border: '1px solid #303030',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Row gutter={[24, 16]}>
            <Col span={24}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>미디어명 <span style={{ color: 'red' }}>*</span></Text>}
                name="mediaName"
                rules={[
                  {
                    validator: (_, value) => {
                      if (!value || value.trim() === '') {
                        return Promise.reject(new Error('미디어명을 입력해주세요.'));
                      }
                      if (value.length > 50) {
                        return Promise.reject(new Error('미디어명은 50자 이하로 입력해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input
                  placeholder="미디어명을 입력하세요"
                  maxLength={50}
                  showCount
                  style={{ background: '#141414', border: '1px solid #303030', color: '#ffffff' }}
                />
              </Form.Item>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '-16px' }}>
                <InfoCircleOutlined style={{ color: '#F24822', fontSize: '14px', flexShrink: 0 }} />
                <Text style={{ fontSize: '12px', color: '#a6a6a6', margin: 0, whiteSpace: 'nowrap' }}>
                  미디어명은 디스플레이에 노출되지 않습니다.
                </Text>
              </div>
            </Col>

            {/* <Col span={24}>
              <Form.Item
                label={<Text style={{ color: '#ffffff' }}>내용</Text>}
                name="content"
              >
                <TextArea
                  placeholder="내용을 입력하세요"
                  rows={4}
                  maxLength={200}
                  showCount
                  style={{
                    background: '#141414',
                    border: '1px solid #303030',
                    color: '#ffffff',
                    resize: 'none'
                  }}
                />
              </Form.Item>
            </Col> */}

            <Col span={24}>
              <Form.Item
                label={
                  <Text style={{ color: '#ffffff' }}>
                    첨부파일 {currentFileDeleted && <span style={{ color: 'red' }}>*</span>}
                  </Text>
                }
                name="videoFile"
                rules={[
                  {
                    validator: () => {
                      if (currentFileDeleted && !videoFile) {
                        return Promise.reject(new Error('기존 파일을 삭제했습니다. 새로운 파일을 업로드해주세요.'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <div style={{ marginBottom: '16px' }}>
                  {mediaDetail.videoFile && !currentFileDeleted && (
                    <div style={{
                      padding: '12px',
                      background: '#2a2a2a',
                      border: '1px solid #404040',
                      borderRadius: '6px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{
                          width: '16px',
                          height: '16px',
                          background: '#F24822',
                          borderRadius: '2px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '10px',
                          color: '#ffffff'
                        }}>
                          📎
                        </div>
                        <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                          현재 파일: {mediaDetail.videoFile}
                        </Text>
                      </div>
                      <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={handleDeleteCurrentFile}
                        style={{
                          color: '#ff4d4f',
                          border: 'none',
                          background: 'transparent',
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                        title="기존 파일 삭제"
                      />
                    </div>
                  )}
                  {currentFileDeleted && mediaDetail.videoFile && (
                    <div style={{
                      padding: '12px',
                      background: '#2d1b1b',
                      border: '1px solid #5a2d2d',
                      borderRadius: '6px',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <Text style={{ color: '#ff7875', fontSize: '14px' }}>
                        삭제 예정: {mediaDetail.videoFile} (수정 시 완전히 삭제됩니다)
                      </Text>
                    </div>
                  )}
                </div>

                <Dragger
                  {...uploadProps}
                  style={{
                    background: '#141414',
                    border: '2px dashed #303030',
                    borderRadius: '8px'
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined style={{ color: '#a6a6a6', fontSize: '48px' }} />
                  </p>
                  <p style={{ color: '#ffffff', fontSize: '16px', margin: '16px 0 8px' }}>
                    업로드할 동영상 선택 또는 드래그
                  </p>
                </Dragger>
              </Form.Item>

              {/* 업로드 진행률 */}
              {isUploading && (
                <div style={{ marginTop: '16px' }}>
                  <Progress
                    percent={Math.round(uploadProgress)}
                    status={uploadProgress === 100 ? 'success' : 'active'}
                    strokeColor={{
                      '0%': '#F24822',
                      '100%': '#52c41a'
                    }}
                    format={(percent) => `${percent}%`}
                  />
                  <Text style={{ color: '#a6a6a6', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    파일을 업로드하고 있습니다...
                  </Text>
                </div>
              )}

              {/* 파일 업로드 안내 */}
              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    background: '#F24822',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '10px',
                    color: '#ffffff'
                  }}>
                    !
                  </div>
                  <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                    파일형식: MP4 / 100MB 이하 (최대 1개) - 새 파일을 선택하지 않으면 기존 파일이 유지됩니다.
                  </Text>
                </div>
                <div style={{ marginLeft: '24px' }}>
                  <Text style={{ color: '#a6a6a6', fontSize: '12px' }}>
                    권장 미디어 사이즈: 3072px(가로) × 864px(세로)
                  </Text>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        {/* 등록 정보 섹션 */}
        {/* <Card
          title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>등록 정보</Text>}
          style={{
            marginBottom: '24px',
            background: '#1f1f1f',
            border: '1px solid #303030',
          }}
          styles={{ body: { padding: '24px' } }}
        >
          <Row gutter={[24, 16]}>
            <Col span={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text style={{ color: '#a6a6a6', fontSize: '13px' }}>등록일</Text>
                <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                  {mediaDetail.createdAt ? new Date(mediaDetail.createdAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).replace(/\//g, '.') : '-'}
                </Text>
              </div>
            </Col>
            <Col span={12}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Text style={{ color: '#a6a6a6', fontSize: '13px' }}>최근 수정일</Text>
                <Text style={{ color: '#ffffff', fontSize: '14px' }}>
                  {mediaDetail.updatedAt ? new Date(mediaDetail.updatedAt).toLocaleDateString('ko-KR', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                  }).replace(/\//g, '.') : '-'}
                </Text>
              </div>
            </Col>
          </Row>
        </Card> */}

        {/* 액션 버튼 */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px'
        }}>
          <Button
            size="large"
            onClick={handleCancel}
            style={{
              minWidth: '100px',
              background: 'transparent',
              border: '1px solid #303030',
              color: '#ffffff'
            }}
          >
            취소
          </Button>
          <Button
            type="primary"
            size="large"
            htmlType="submit"
            loading={loading}
            style={{
              minWidth: '100px',
              background: '#F24822',
              border: 'none'
            }}
          >
            수정
          </Button>
        </div>
      </Form>

      {/* 미리보기 모달 */}
      <MediaPreviewModal
        visible={previewVisible}
        onClose={handlePreviewClose}
        media={mediaDetail}
        videoUrl={previewVideoUrl}
      />
    </div>
  );
};

export default MediaEdit;